import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  PaperAirplaneIcon,
  SparklesIcon,
  UserCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../../contexts/AuthContext";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import {
  queryLegalAssistantStream,
  validateQuestion,
} from "@/shared/ai/legal-assistant";
import {
  getEnabledCountries,
  type CountryConfig,
} from "@/shared/config/countries";
import { toast } from "sonner";
import { Select, Button } from "@/shared/ui";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface ConversationHistory {
  id: string;
  title: string;
  messages: Message[];
  country: string;
  createdAt: Date;
  lastMessageAt: Date;
}

export const LegalChatPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const { checkLimit, trackUsage } = usePlanLimits();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [countries, setCountries] = useState<CountryConfig[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // History states
  const [conversationHistory, setConversationHistory] = useState<
    ConversationHistory[]
  >([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedConversation, setSelectedConversation] =
    useState<ConversationHistory | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const language = i18n.language as "es" | "en";

  // Load countries from database
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const enabledCountries = await getEnabledCountries();
        setCountries(enabledCountries);
        if (enabledCountries.length > 0) {
          setSelectedCountry(enabledCountries[0].code);
        }
      } catch (error) {
        console.error("Error loading countries:", error);
        toast.error("Error al cargar países disponibles");
      } finally {
        setLoadingCountries(false);
      }
    };
    loadCountries();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Save conversation to history when it changes
  useEffect(() => {
    if (messages.length > 0) {
      const conversationId = "current";
      const title =
        messages[0]?.content.substring(0, 50) + "..." || "Nueva conversación";

      setConversationHistory((prev) => {
        const existing = prev.find((c) => c.id === conversationId);
        if (existing) {
          return prev.map((c) =>
            c.id === conversationId
              ? {
                  ...c,
                  messages,
                  lastMessageAt: new Date(),
                }
              : c,
          );
        } else {
          return [
            {
              id: conversationId,
              title,
              messages,
              country: selectedCountry,
              createdAt: new Date(),
              lastMessageAt: new Date(),
            },
            ...prev,
          ];
        }
      });
    }
  }, [messages, selectedCountry]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading || !selectedCountry) return;

    const userMessage = input.trim();
    setInput("");

    // Validate question
    const validation = validateQuestion(userMessage, language);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    // Check limits
    const hasAccess = await checkLimit("legal_ai_chat");
    if (!hasAccess) {
      toast.error(t("legalChat.errors.rateLimit"));
      return;
    }

    // Add user message
    const newUserMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      // Create assistant message placeholder
      const assistantMessageId = (Date.now() + 1).toString();
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Build conversation history
      const conversationHistory = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      // Stream response
      const stream = queryLegalAssistantStream({
        query: userMessage,
        country: selectedCountry,
        language,
        conversationHistory,
      });

      for await (const chunk of stream) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: msg.content + chunk }
              : msg,
          ),
        );
      }

      // Track usage
      await trackUsage("legal_ai_chat");
    } catch (error: any) {
      console.error("Error querying legal assistant:", error);

      // Remove failed assistant message
      setMessages((prev) => prev.slice(0, -1));

      if (error?.message?.includes("Rate limit")) {
        toast.error(t("legalChat.errors.rateLimit"));
      } else if (error?.message?.includes("network")) {
        toast.error(t("legalChat.errors.network"));
      } else {
        toast.error(t("legalChat.errors.generic"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleViewConversation = (conversation: ConversationHistory) => {
    setSelectedConversation(conversation);
    setShowHistoryModal(false);
    setShowDetailModal(true);
  };

  const handleLoadConversation = (conversation: ConversationHistory) => {
    setMessages(conversation.messages);
    setSelectedCountry(conversation.country);
    setShowDetailModal(false);
    toast.success("Conversación cargada");
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="shrink-0 bg-tiber-600 p-4 sm:p-5 border-b border-tiber-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 sm:p-3 bg-white/10 rounded-lg">
              <img
                src="../../images/brand/lawgentia-logo-white.png"
                alt="Lawgentia Logo"
                className="w-8 h-8 sm:w-10 sm:h-10"
              />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                {t("legalChat.title")}
              </h1>
              <p className="text-sm text-white/80">{t("legalChat.subtitle")}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* History Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistoryModal(true)}
              leftIcon={<ClockIcon className="w-5 h-5" />}
              className="text-white hover:bg-white/10"
            >
              Historial
            </Button>

            {/* Country Selector */}
            <div className="min-w-[180px]">
              <Select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                disabled={loadingCountries}
                options={countries.map((country) => ({
                  value: country.code,
                  label: `${country.flag} ${language === "es" ? country.nameEs : country.nameEn}`,
                }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-romance-50">
        {messages.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center py-12">
            <div className="p-8 bg-white rounded-lg shadow border border-tiber-200">
              <h2 className="text-xl font-bold text-tiber-900 mb-3">
                {t("legalChat.welcome.title")}
              </h2>
              <p className="text-base text-tiber-700 mb-6">
                {t("legalChat.welcome.message")}
              </p>

              <ul className="text-left space-y-2 max-w-md mx-auto">
                {(
                  t("legalChat.welcome.points", {
                    returnObjects: true,
                  }) as string[]
                ).map((point, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-tiber-700"
                  >
                    <span className="text-tiber-600 text-lg">✓</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.role === "assistant" && (
                <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-tiber-600 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-white" />
                </div>
              )}

              <div
                className={`max-w-[80%] sm:max-w-[70%] px-4 py-3 rounded-lg ${
                  message.role === "user"
                    ? "bg-tiber-600 text-white"
                    : "bg-white text-tiber-900 border border-tiber-200"
                }`}
              >
                {message.role === "assistant" && !message.content && isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="w-2 h-2 bg-tiber-600 rounded-full"
                          animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.5, 1, 0.5],
                          }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            delay: i * 0.2,
                          }}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-tiber-600">
                      {t("legalChat.writing")}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm sm:text-base whitespace-pre-wrap">
                    {message.content}
                  </p>
                )}
                {!(message.role === "assistant" && !message.content && isLoading) && (
                  <p
                    className={`text-xs mt-1.5 ${
                      message.role === "user" ? "text-white/70" : "text-tiber-500"
                    }`}
                  >
                    {message.timestamp.toLocaleTimeString(language, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                )}
              </div>

              {message.role === "user" && (
                <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-tiber-700 flex items-center justify-center overflow-hidden">
                  {profile?.profile_image_url ? (
                    <img
                      src={profile.profile_image_url}
                      alt="User"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserCircleIcon className="w-6 h-6 text-white" />
                  )}
                </div>
              )}
            </motion.div>
          ))
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Disclaimer */}
      <div className="shrink-0 px-4 py-2 bg-amber-50 border-t border-amber-200">
        <p className="text-xs text-amber-900 text-center flex items-center justify-center gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
          <span>{t("legalChat.disclaimer")}</span>
        </p>
      </div>

      {/* Fixed Input Area */}
      <div className="shrink-0 bg-white border-t border-tiber-200 p-4">
        <div className="flex gap-3 max-w-4xl mx-auto">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t("legalChat.placeholder")}
            disabled={isLoading || loadingCountries}
            className="flex-1 px-4 py-3 text-sm border border-tiber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiber-600 focus:border-transparent disabled:opacity-50"
          />
          <Button
            onClick={handleSendMessage}
            disabled={!input.trim() || isLoading || loadingCountries}
            variant="primary"
            className="shrink-0"
            leftIcon={<PaperAirplaneIcon className="w-5 h-5" />}
          >
            {t("legalChat.send")}
          </Button>
        </div>
      </div>

      {/* History List Modal */}
      <AnimatePresence>
        {showHistoryModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowHistoryModal(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            <motion.div
              className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 z-10 max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowHistoryModal(false)}
                className="absolute top-4 right-4 text-titan hover:text-tiber-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                Historial de Conversaciones
              </h2>

              <div className="space-y-3">
                {conversationHistory.length === 0 ? (
                  <p className="text-center text-tiber-600 py-8">
                    No hay conversaciones guardadas
                  </p>
                ) : (
                  conversationHistory.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => handleViewConversation(conv)}
                      className="p-4 bg-romance-50 rounded-lg border border-tiber-200 hover:border-tiber-600 cursor-pointer transition-colors"
                    >
                      <h3 className="font-semibold text-tiber-900 mb-1">
                        {conv.title}
                      </h3>
                      <p className="text-sm text-tiber-600">
                        {conv.messages.length} mensajes •{" "}
                        {conv.lastMessageAt.toLocaleDateString(language)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Conversation Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedConversation && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDetailModal(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            <motion.div
              className="relative bg-white rounded-lg shadow-2xl max-w-3xl w-full p-6 z-10 max-h-[80vh] flex flex-col"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-tiber-900">
                  {selectedConversation.title}
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-titan hover:text-tiber-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                {selectedConversation.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-2 ${
                      msg.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                        msg.role === "user"
                          ? "bg-tiber-600 text-white"
                          : "bg-tiber-100 text-tiber-900"
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 justify-end border-t pt-4">
                <Button
                  variant="secondary"
                  onClick={() => setShowDetailModal(false)}
                >
                  Cerrar
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleLoadConversation(selectedConversation)}
                >
                  Cargar Conversación
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
